import { BRAND_SECONDARY } from "../../../utils/Styles";
import React from "react";
import {
    Calendar,
    Callout,
    DayOfWeek,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    DirectionalHint,
    Fabric,
    FocusTrapZone,
    IColumn,
    IDetailsListProps,
    IDetailsRowStyles,
    PrimaryButton,
    SelectionMode,
    Stack,
    Text,
} from "@fluentui/react";
import {
    mdiDotsVertical,
} from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import { IMiscellaneousFile } from "../../../vm";
import { Loading } from "../../../services/Loading";
import {
    childrenGap,
    childrenGap8,
    DayPickerStrings,
} from "../../common/Constants";
import NoDataTile from "../../common/NoDataTile";
import {
    deleteMiscellaneousFile,
    getMiscellaneousFiles,
} from "../../../services/customers/CustomerService";
import ManageMiscellaneousFilesDialog from "../miscellaneous-files/ManageMiscellaneousFilesDialog";
import CustomDrawer from "../../common/CustomDrawer";
import { parseJwt } from "../../../services/UtilService";

let calendarButtonElement: HTMLElement;

export interface ViewUploadedReportDialogProps {
    customerId: string;
    datacenterId: string;
    datacenterName: string;
    date: Date | string;
    onDialogClose: Function;
};

const ViewUploadedReportDialog: React.FC<ViewUploadedReportDialogProps> = ({
    customerId,
    datacenterId,
    datacenterName,
    date,
    onDialogClose
}) => {
    // custom hooks
    const { showToast } = React.useContext(ToastContext);
    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
    const tokenData = parseJwt();

    // state
    const [state, setState] = React.useState({
        miscellaneousFiles: [],
        isLoading: false,
        search: {
            date: new Date(date),
            customerId,
            datacenterId,
            fromPowerUtilizationPage: true
        },
        showCallender: false,
        manageActionsCallout: {
            isOpen: false,
            element: undefined,
            index: undefined,
            data: undefined,
        },
        manageMfDialog: {
            isOpen: false,
            index: -1,
            data: undefined
        }
    } as {
        miscellaneousFiles: IMiscellaneousFile[];
        manageMfDialog: {
            isOpen: boolean;
            index?: number;
            data?: IMiscellaneousFile;
        };
        isLoading: boolean;
        search: {
            date: Date | string;
            customerId: string;
            datacenterId: string;
            fromPowerUtilizationPage: boolean;
        };
        showCallender: boolean;
        manageActionsCallout: {
            isOpen: boolean;
            element: any;
            index?: number;
            data?: IMiscellaneousFile;
        };
    });

    // hooks
    React.useEffect(() => {
        const asyncFunc = async () => {
            await getAllMiscellaneousFiles();
        };
        asyncFunc();
    }, []);

    const getAllMiscellaneousFiles = async () => {
        setState({ ...state, isLoading: true });
        let tempSearch = { ...state.search };
        tempSearch.date = moment(tempSearch.date).format("YYYY-MM-DD");
        let result = await getMiscellaneousFiles(tempSearch);
        if (result && result.success) {
            setState({
                ...state,
                miscellaneousFiles: result.data,
                isLoading: false,
            });
        } else {
            showToast(result?.message || "Error while getting files", "error");
            setState({ ...state, isLoading: false });
        }
    };

    const handleDialogOpen = (isOpen: boolean, index?: number) => {
        let miscellaneousFiles: any = { datacenterId };
        if (index !== undefined) {
            miscellaneousFiles = { ...state.miscellaneousFiles[index] };
        }
        setState({
            ...state,
            manageMfDialog: {
                isOpen,
                index,
                data: miscellaneousFiles,
            },
        });
    };

    const handleDialogClose = (data: any) => {
        let miscellaneousFiles = [...state.miscellaneousFiles];
        if (data) {
            if (state.manageMfDialog.index !== undefined) {
                miscellaneousFiles[state.manageMfDialog.index] = data;
                showToast("Power utilization file uploaded successfully", "success");
            } else {
                if (data?.length > 0) {
                    if (
                        moment(data[0].date).format("MMM-YYYY") ===
                        moment(state.search.date).format("MMM-YYYY")
                    ) {
                        miscellaneousFiles.unshift(data[0]);

                        showToast("Power utilization file uploaded successfully", "success");
                    } else {
                        showToast("Power utilization file uploaded successfully", "success");
                    }
                }
            }
        }
        setState({
            ...state,
            manageMfDialog: {
                isOpen: false,
                index: undefined,
                data: undefined,
            },
            miscellaneousFiles,
        });
    };

    const handleLoading = () => {
        setState((prev) => ({
            ...prev,
            isLoading: !prev.isLoading,
        }));
    };

    const deletePmsFile = async (item: IMiscellaneousFile, index: number) => {
        showConfirmDialog(
            "Are You Sure",
            "Do you want to delete this file",
            async () => {
                setState({
                    ...state,
                    isLoading: true,
                    manageActionsCallout: {
                        isOpen: false,
                        index: undefined,
                        element: undefined,
                        data: undefined,
                    },
                });
                let result = await deleteMiscellaneousFile(item);
                if (result.success) {
                    showToast("Power utilization file deleted successfully", "success");
                    let miscellaneousFiles = [...state.miscellaneousFiles];
                    miscellaneousFiles.splice(index, 1);
                    setState({
                        ...state,
                        miscellaneousFiles,
                        isLoading: false,
                        manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                        },
                    });
                } else {
                    setState({
                        ...state,
                        isLoading: false,
                        manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                        },
                    });
                    showToast(result.message || "Error while deleting file", "error");
                }
            }
        );
    };

    const _renderItemColumn = (item: any, index: number, column: IColumn) => {
        switch (column.key) {
            case "actions":
                return (
                    <span
                        onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
                    >
                        <Icon
                            path={mdiDotsVertical}
                            size={0.9}
                            className="pointer"
                            color={BRAND_SECONDARY}
                        />
                    </span>
                );
            case "fileUrl":
                return (
                    <a
                        href={item.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download="aaa.xlsx"
                    >
                        {String(item.file).split("[separator]").length > 1
                            ? String(item.file).split("[separator]")[
                            String(item.file).split("[separator]").length - 1
                            ]
                            : "Download"}
                    </a>
                );
            case "date":
                return (
                    <Text style={{ fontWeight: 600 }}>
                        {moment(item.date).format("MMM-YYYY")}
                    </Text>
                );
            default:
                return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
        }
    };

    const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
        const customStyles: Partial<IDetailsRowStyles> = {};
        if (props) {
            if (props.itemIndex % 2 !== 0) {
                customStyles.root = { backgroundColor: "#F3F3F3" };
            }

            return <DetailsRow {...props} styles={customStyles} />;
        }
        return null;
    };

    const closeCallender = (ev?: any) => {
        setState({
            ...state,
            showCallender: false,
        });
    };

    const onSearch = async () => {
        await getAllMiscellaneousFiles();
    };

    const handleCallOutOpenClose = (
        isOpen: boolean,
        ele?: React.MouseEvent<HTMLSpanElement>,
        index?: number,
        data?: IMiscellaneousFile
    ) => {
        setState({
            ...state,
            manageActionsCallout: {
                isOpen,
                element: isOpen ? ele?.currentTarget : undefined,
                index,
                data,
            },
        });
    };
    return (
        <React.Fragment>
            {state.isLoading && <Loading />}
            <CustomDrawer
                title={`List of uploaded reports for ${datacenterName}`}
                handelOpen={true}
                onClose={() => onDialogClose()}
            >
                <Stack tokens={childrenGap}>
                    <Stack.Item>
                        <Stack horizontal wrap tokens={childrenGap}>
                            <Stack.Item>
                                <div
                                    ref={(calendarBtn) => (calendarButtonElement = calendarBtn!)}
                                >
                                    <DefaultButton
                                        text={
                                            !state.search.date
                                                ? "Select a Month"
                                                : moment(state.search.date).format("MMM-YYYY")
                                        }
                                        onClick={() => setState({ ...state, showCallender: true })}
                                        className="month-picker-btn"
                                    />
                                </div>
                                {state.showCallender && (
                                    <Callout
                                        isBeakVisible={false}
                                        gapSpace={0}
                                        doNotLayer={false}
                                        target={calendarButtonElement}
                                        directionalHint={DirectionalHint.bottomLeftEdge}
                                        onDismiss={() => closeCallender()}
                                        setInitialFocus
                                    >
                                        <FocusTrapZone
                                            firstFocusableSelector="ms-DatePicker-day--today"
                                            isClickableOutsideFocusTrap
                                        >
                                            <Calendar
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onSelectDate={(date: Date | null | undefined) => {
                                                    setState({
                                                        ...state,
                                                        search: {
                                                            ...state.search,
                                                            date: date as Date,
                                                        },
                                                        showCallender: false,
                                                    });
                                                }}
                                                onDismiss={() => closeCallender()}
                                                isMonthPickerVisible={true}
                                                value={new Date(state.search.date)}
                                                firstDayOfWeek={DayOfWeek.Sunday}
                                                strings={DayPickerStrings}
                                                isDayPickerVisible={false}
                                                highlightCurrentMonth={false}
                                                highlightSelectedMonth={true}
                                                showMonthPickerAsOverlay={false}
                                                showGoToToday={false}
                                            />
                                        </FocusTrapZone>
                                    </Callout>
                                )}
                            </Stack.Item>
                            <Stack.Item>
                                <PrimaryButton text="Search" onClick={onSearch} />
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>
                    {![4].includes(tokenData.role) && (
                        // {![2, 4].includes(tokenData.role) && (
                        <Stack.Item align="end">
                            <PrimaryButton
                                text="Add Power File"
                                onClick={() => handleDialogOpen(true)}
                                className="borderless-btn bg-white"
                            />
                        </Stack.Item>
                    )}
                    <Stack.Item>
                        {state.miscellaneousFiles && state.miscellaneousFiles.length > 0 ? (
                            <Fabric>
                                <DetailsList
                                    items={state.miscellaneousFiles}
                                    columns={
                                        ![4].includes(tokenData.role)
                                            // ![2, 4].includes(tokenData.role)
                                            ? [
                                                {
                                                    key: "fileUrl",
                                                    name: "File",
                                                    fieldName: "fileUrl",
                                                    minWidth: 350,
                                                    maxWidth: 450,
                                                    isResizable: true,
                                                },
                                                {
                                                    key: "actions",
                                                    name: "Actions",
                                                    fieldName: "actions",
                                                    minWidth: 80,
                                                    maxWidth: 110,
                                                    isResizable: true,
                                                },
                                            ]
                                            : [
                                                {
                                                    key: "fileUrl",
                                                    name: "File",
                                                    fieldName: "fileUrl",
                                                    minWidth: 350,
                                                    maxWidth: 450,
                                                    isResizable: true,
                                                },
                                            ]
                                    }
                                    onRenderItemColumn={_renderItemColumn as any}
                                    compact={true}
                                    setKey="set"
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selectionMode={SelectionMode.none}
                                    onRenderRow={_onRenderRow}
                                />
                            </Fabric>
                        ) : (
                            <NoDataTile title="No Files Found For this Month"></NoDataTile>
                        )}
                    </Stack.Item>
                </Stack>
            </CustomDrawer>
            {state.manageActionsCallout.isOpen && (
                <Callout
                    styles={{ root: { width: 95, padding: 20 } }}
                    role="alertdialog"
                    ariaLabelledBy={
                        state.manageActionsCallout && state.manageActionsCallout.data?._id
                    }
                    gapSpace={0}
                    target={state.manageActionsCallout.element}
                    directionalHint={DirectionalHint.leftCenter}
                    onDismiss={() => handleCallOutOpenClose(false)}
                // setInitialFocus={true}
                >
                    <Stack tokens={childrenGap8}>
                        <Stack.Item className="pointer">
                            <span
                                onClick={() => {
                                    handleDialogOpen(true, state.manageActionsCallout.index);
                                }}
                                className="callout-options"
                            >
                                Edit
                            </span>
                        </Stack.Item>
                        <Stack.Item className="pointer">
                            <span
                                onClick={() => {
                                    deletePmsFile(
                                        state.manageActionsCallout.data as IMiscellaneousFile,
                                        state.manageActionsCallout.index as number
                                    );
                                }}
                                className="callout-options"
                            >
                                Delete
                            </span>
                        </Stack.Item>
                    </Stack>
                </Callout>
            )}
            {state.manageMfDialog.isOpen && (
                <ManageMiscellaneousFilesDialog
                    customerId={state.search.customerId}
                    data={state.manageMfDialog.data}
                    handleLoading={handleLoading}
                    handleDialogClose={(data: any) => handleDialogClose(data)}
                    datacenterList={[{ key: datacenterId, name: datacenterName }]}
                    miscellaneousFiles={state.miscellaneousFiles}
                    date={state.search.date}
                    type="power"
                />
            )}
        </React.Fragment>
    );
};

export default ViewUploadedReportDialog;
